<div class="story-wrapper">
    <app-site-nav [showDock]="false"></app-site-nav>

    <!-- First screen: connect LIAM to ChatGPT, Claude or Muse. The story follows. -->
    <app-connect-hero></app-connect-hero>

    <!-- Chapter 1: The Opening -->
    <section class="story-chapter hero-chapter" id="why">
        <div class="chapter-content">
            <!-- Heartbeat Animation Icon -->
            <div class="hero-icon">
                <div class="tri-dots-anim">
                    <div class="dot dot-1"></div>
                    <div class="line line-1-2"></div>
                    <div class="dot dot-2"></div>
                    <div class="line line-2-3"></div>
                    <div class="dot dot-3"></div>
                </div>
            </div>
            <h1 class="hero-headline">
                We remember <em>everything</em>...
            </h1>
            <p class="hero-subline">
                ...except when it matters most.
            </p>
            <div class="scroll-indicator">
                <span>Scroll to begin</span>
                <div class="scroll-arrow"></div>
            </div>
        </div>
    </section>

    <!-- Chapter 2: The Problem - Narrative -->
    <section class="story-chapter narrative-chapter" id="story">
        <div class="narrative-block fade-in">
            <p class="narrative-text large">
                Think back to last Tuesday.
            </p>
        </div>
        <div class="narrative-block fade-in">
            <p class="narrative-text">
                What did you have for lunch? Who did you talk to?<br>
                What brilliant idea crossed your mind at 3pm?
            </p>
        </div>
        <div class="narrative-block fade-in">
            <p class="narrative-text muted">
                It's already fading, isn't it?
            </p>
        </div>
    </section>

    <!-- Chapter 3: The Modern Problem -->
    <section class="story-chapter narrative-chapter">
        <div class="narrative-block fade-in">
            <p class="narrative-text large">
                We live in an age of <em>infinite information</em>.
            </p>
        </div>
        <div class="narrative-block fade-in">
            <p class="narrative-text">
                We capture photos we never look at.<br>
                We bookmark articles we never read.<br>
                We save notes we can never find.
            </p>
        </div>
        <div class="narrative-block fade-in">
            <p class="narrative-text accent">
                Our digital lives are overflowing.<br>
                Our memories are slipping away.
            </p>
        </div>
    </section>

    <!-- Chapter 4: The Moments We Lose -->
    <section class="story-chapter moments-chapter" id="moments">
        <div class="chapter-header fade-in">
            <h2 class="chapter-title">The moments we lose</h2>
        </div>

        <div class="moments-grid">
            <div class="moment-card fade-in">
                <div class="moment-scene">
                    <span class="moment-icon">💭</span>
                </div>
                <div class="moment-story">
                    <p class="moment-quote">"What was that restaurant Sarah mentioned last month?"</p>
                    <p class="moment-context">You scrolled through 200 messages. Never found it.</p>
                </div>
            </div>

            <div class="moment-card fade-in">
                <div class="moment-scene">
                    <span class="moment-icon">🎁</span>
                </div>
                <div class="moment-story">
                    <p class="moment-quote">"I know he mentioned his favorite whiskey..."</p>
                    <p class="moment-context">You bought a gift card instead.</p>
                </div>
            </div>

            <div class="moment-card fade-in">
                <div class="moment-scene">
                    <span class="moment-icon">💡</span>
                </div>
                <div class="moment-story">
                    <p class="moment-quote">"I had this amazing idea in the shower..."</p>
                    <p class="moment-context">Gone before the coffee was ready.</p>
                </div>
            </div>

            <div class="moment-card fade-in">
                <div class="moment-scene">
                    <span class="moment-icon">📋</span>
                </div>
                <div class="moment-story">
                    <p class="moment-quote">"We discussed this in the Q2 meeting..."</p>
                    <p class="moment-context">Three hours of searching. Still not sure.</p>
                </div>
            </div>

            <div class="moment-card fade-in">
                <div class="moment-scene">
                    <span class="moment-icon">🎂</span>
                </div>
                <div class="moment-story">
                    <p class="moment-quote">"Wait, when is Mom's doctor appointment?"</p>
                    <p class="moment-context">It was yesterday.</p>
                </div>
            </div>

            <div class="moment-card fade-in">
                <div class="moment-scene">
                    <span class="moment-icon">📚</span>
                </div>
                <div class="moment-story">
                    <p class="moment-quote">"That book completely changed my perspective on..."</p>
                    <p class="moment-context">You can't remember the title anymore.</p>
                </div>
            </div>
        </div>
    </section>

    <!-- Chapter 5: The Question -->
    <section class="story-chapter narrative-chapter dark-chapter">
        <div class="narrative-block fade-in">
            <p class="narrative-text large light">
                What if you never had to forget?
            </p>
        </div>
        <div class="narrative-block fade-in">
            <p class="narrative-text light">
                What if every conversation, every idea, every moment<br>
                was always within reach?
            </p>
        </div>
        <div class="narrative-block fade-in">
            <p class="narrative-text muted-light">
                What if your memory could finally keep up with your life?
            </p>
        </div>
    </section>

    <!-- Chapter 6: The Solution -->
    <section class="story-chapter solution-chapter">
        <div class="solution-reveal fade-in">
            <div class="liam-logo-large">
                <svg viewBox="0 0 80 80" fill="none">
                    <rect width="80" height="80" rx="18" fill="#f97316" />
                    <path d="M25 18V62H55" stroke="white" stroke-width="6" stroke-linecap="round"
                        stroke-linejoin="round" />
                    <circle cx="25" cy="18" r="5" fill="white" />
                    <circle cx="55" cy="62" r="5" fill="white" />
                    <circle cx="40" cy="62" r="3" fill="white" opacity="0.6" />
                    <circle cx="25" cy="40" r="3" fill="white" opacity="0.6" />
                </svg>
            </div>
            <h2 class="solution-title">Meet LIAM</h2>
            <p class="solution-tagline">Your memory, extended.</p>
        </div>

        <div class="solution-description fade-in">
            <p class="narrative-text">
                LIAM isn't another notes app. It's not a task manager.<br>
                It's not trying to organize your life into folders and tags.
            </p>
        </div>

        <div class="solution-description fade-in">
            <p class="narrative-text accent">
                LIAM simply remembers — so you don't have to.
            </p>
        </div>
    </section>

    <!-- Chapter 7: How It Works -->
    <section class="story-chapter how-chapter" id="how-it-works">
        <div class="chapter-header fade-in">
            <h2 class="chapter-title">How it works</h2>
            <p class="chapter-subtitle">As natural as talking to a friend</p>
        </div>

        <div class="how-flow">
            <!-- Step 1 -->
            <div class="how-step fade-in">
                <div class="step-conversation">
                    <div class="chat-bubble you">
                        Remember that John's birthday is March 15th and he loves single malt whiskey
                    </div>
                    <div class="chat-bubble liam">
                        <span class="liam-name">LIAM</span>
                        Got it. I'll remember John's birthday and his taste in whiskey.
                    </div>
                </div>
                <div class="step-label">
                    <span class="step-number">01</span>
                    <span class="step-title">Just tell LIAM</span>
                    <p class="step-text">No forms. No categories. Just say it naturally.</p>
                </div>
            </div>

            <!-- Step 2 -->
            <div class="how-step reverse fade-in">
                <div class="step-conversation">
                    <div class="chat-bubble you">
                        Had dinner at Osteria Francescana — best truffle pasta I've ever had
                    </div>
                    <div class="chat-bubble liam">
                        <span class="liam-name">LIAM</span>
                        Noted! Osteria Francescana with amazing truffle pasta. I'll remember this for your Italian food
                        recommendations.
                    </div>
                </div>
                <div class="step-label">
                    <span class="step-number">02</span>
                    <span class="step-title">LIAM understands context</span>
                    <p class="step-text">It knows that's a restaurant. It knows you love Italian food. It connects the
                        dots.</p>
                </div>
            </div>

            <!-- Step 3 -->
            <div class="how-step fade-in">
                <div class="step-conversation">
                    <div class="chat-bubble you">
                        What should I get John for his birthday?
                    </div>
                    <div class="chat-bubble liam">
                        <span class="liam-name">LIAM</span>
                        John's birthday is coming up on March 15th. You mentioned he loves single malt whiskey — maybe a
                        nice bottle of Macallan or Lagavulin?
                    </div>
                </div>
                <div class="step-label">
                    <span class="step-number">03</span>
                    <span class="step-title">Ask anything, anytime</span>
                    <p class="step-text">LIAM recalls what matters, when it matters.</p>
                </div>
            </div>
        </div>
    </section>

    <!-- Chapter 8: More Examples -->
    <section class="story-chapter examples-chapter">
        <div class="chapter-header fade-in">
            <h2 class="chapter-title">Imagine remembering...</h2>
        </div>

        <div class="examples-list">
            <div class="example-item fade-in">
                <div class="example-query">
                    <span class="query-icon">🍽️</span>
                    <p>"What's that sushi place Mike recommended in Tokyo?"</p>
                </div>
                <div class="example-answer">
                    <p>Sukiyabashi Jiro — Mike said to book 3 months ahead and order the omakase.</p>
                </div>
            </div>

            <div class="example-item fade-in">
                <div class="example-query">
                    <span class="query-icon">💊</span>
                    <p>"What did Dr. Chen say about my blood pressure?"</p>
                </div>
                <div class="example-answer">
                    <p>On your March 3rd visit, she said to reduce sodium intake and recheck in 6 weeks. She recommended
                        the DASH diet.</p>
                </div>
            </div>

            <div class="example-item fade-in">
                <div class="example-query">
                    <span class="query-icon">🏠</span>
                    <p>"What paint color did we use in the living room?"</p>
                </div>
                <div class="example-answer">
                    <p>Benjamin Moore "Simply White" OC-117. You bought it from the hardware store on Oak Street.</p>
                </div>
            </div>

            <div class="example-item fade-in">
                <div class="example-query">
                    <span class="query-icon">👶</span>
                    <p>"When did Emma take her first steps?"</p>
                </div>
                <div class="example-answer">
                    <p>November 14th, 2024 — right after breakfast. She walked from the couch to the coffee table.</p>
                </div>
            </div>

            <div class="example-item fade-in">
                <div class="example-query">
                    <span class="query-icon">💼</span>
                    <p>"What did we agree on for the marketing budget?"</p>
                </div>
                <div class="example-answer">
                    <p>In the October planning meeting, you settled on $50K for Q1, with $15K allocated specifically for
                        social media campaigns.</p>
                </div>
            </div>

            <div class="example-item fade-in">
                <div class="example-query">
                    <span class="query-icon">🎬</span>
                    <p>"What was that documentary about minimalism?"</p>
                </div>
                <div class="example-answer">
                    <p>"The Minimalists: Less Is Now" on Netflix. You watched it in January and said it inspired you to
                        declutter the garage.</p>
                </div>
            </div>
        </div>
    </section>

    <!-- Chapter 9: The Vision -->
    <section class="story-chapter vision-chapter">
        <div class="narrative-block fade-in">
            <p class="narrative-text large">
                Your notes app was built for <em>storing</em>.
            </p>
        </div>
        <div class="narrative-block fade-in">
            <p class="narrative-text large accent">
                LIAM was built for <em>remembering</em>.
            </p>
        </div>
        <div class="narrative-block fade-in">
            <p class="narrative-text">
                Because life isn't organized in folders.<br>
                It's a tapestry of moments, connections, and context.
            </p>
        </div>
        <div class="narrative-block fade-in">
            <p class="narrative-text muted">
                And every thread deserves to be remembered.
            </p>
        </div>
    </section>

    <!-- Chapter 10: CTA -->
    <section class="story-chapter cta-chapter">
        <div class="cta-content fade-in">
            <h2 class="cta-headline">Ready to remember everything?</h2>
            <p class="cta-subtext">Start your journey today. Free forever for personal use.</p>

            <a routerLink="/login" class="btn-cta">
                Get Started
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
            </a>

            <div class="trust-signals">
                <span class="trust-item">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                        <path fill-rule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clip-rule="evenodd" />
                    </svg>
                    No credit card required
                </span>
                <span class="trust-item">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                        <path fill-rule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clip-rule="evenodd" />
                    </svg>
                    Your data stays private
                </span>
                <span class="trust-item">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                        <path fill-rule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clip-rule="evenodd" />
                    </svg>
                    Cancel anytime
                </span>
            </div>
        </div>
    </section>

    <!-- Footer -->
    <footer class="story-footer">
        <div class="footer-content">
            <div class="footer-brand">
                <svg viewBox="0 0 80 80" fill="none" width="40" height="40">
                    <rect width="80" height="80" rx="18" fill="#f97316" />
                    <path d="M25 18V62H55" stroke="white" stroke-width="6" stroke-linecap="round"
                        stroke-linejoin="round" />
                    <circle cx="25" cy="18" r="5" fill="white" />
                    <circle cx="55" cy="62" r="5" fill="white" />
                </svg>
                <span class="footer-title">LIAM</span>
            </div>
            <p class="footer-tagline">Your memory, extended.</p>
            <div class="footer-links">
                <a [href]="links.privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                <span class="divider">•</span>
                <a [href]="links.terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                <span class="divider">•</span>
                <a [href]="links.support" target="_blank" rel="noopener noreferrer">Support</a>
                <span class="divider">•</span>
                <a routerLink="/login">Sign In</a>
            </div>
            <p class="footer-copyright">© 2025 LIAM. All rights reserved.</p>
        </div>
    </footer>
</div>